import React from "react";
import {Box, MenuItem, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";

import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {CAPABILITIES, ChartKind, ChartSpec, ChartSlot, availableCharts, capabilityOf,
        defaultSpec} from "./chartSpec";
import ChartTuning from "./ChartTuning";
import {ResultColumn} from "./reportApi";

/**
 * Building a chart by putting columns on shelves.
 *
 * THE GESTURE ALREADY EXISTS IN THIS APP. ReportShelves teaches it once -- put a field on a shelf
 * and it appears in the result -- and this is the same gesture one level up: put a RESULT COLUMN on
 * a CHART ENCODING. Nobody learns a second interaction, and the shelves change with the chart
 * rather than offering slots that do nothing.
 *
 * THE SHELVES COME FROM THE CAPABILITY TABLE, not from a switch on the kind. A chart type declares
 * what it needs once, in chartSpec, and that single declaration decides which types are offered,
 * what the refusal says when one is not, and which shelves appear here. Adding a chart is adding a
 * row to that table.
 *
 * NOTHING HERE RE-RUNS THE REPORT. The rows are already on the page; a specification is presentation
 * and changing it redraws from what is in hand. That is a correctness rule rather than a
 * performance one -- fiddling with a chart must never record a run, because a run is EVIDENCE and
 * evidence should not accumulate because somebody tried a pie.
 */

type Props = {
    spec: ChartSpec;
    columns: ResultColumn[];
    rows: unknown[][];
    onChange: (spec: ChartSpec) => void;
};

const SHELF = {
    border: "1px dashed #D5DCE3", borderRadius: "4px", minHeight: 38,
    display: "flex", alignItems: "center", flexWrap: "wrap" as const, gap: 0.5, p: 0.75,
};

export default function ChartBuilder({spec, columns, rows, onChange}: Props) {
    const capability = capabilityOf(spec.kind);
    const offered = availableCharts(columns, rows);

    /** Switching kind starts from that kind's default rather than carrying the old encodings:
     *  a category column means nothing on a scatter, and a half-migrated specification is how a
     *  reader ends up looking at a refusal they did not cause. */
    const chooseKind = (kind: ChartKind) => onChange(defaultSpec(kind, columns));

    const put = (slot: ChartSlot, key: string) => {
        const held = spec.encodings[slot.id] ?? [];
        if (held.includes(key)) return;
        onChange({...spec, encodings: {...spec.encodings,
            [slot.id]: slot.multiple ? [...held, key] : [key]}});
    };

    const take = (slot: ChartSlot, key: string) => {
        onChange({...spec, encodings: {...spec.encodings,
            [slot.id]: (spec.encodings[slot.id] ?? []).filter(one => one !== key)}});
    };

    const setOption = (id: string, value: boolean | string | number) =>
        onChange({...spec, options: {...spec.options, [id]: value}});

    /** Columns this slot could take that are not already on it. */
    const availableFor = (slot: ChartSlot) => columns.filter(column =>
        (column.Role ?? "dimension") === slot.accepts
        && !(spec.encodings[slot.id] ?? []).includes(column.Key));

    return (
        <Box sx={{border: "1px solid #E4E7EC", borderRadius: "4px", p: 2, mb: 2}}>

            {/* THE CHART STRIP. A type this result cannot support is disabled WITH ITS REASON in
                the tooltip -- the rule this app keeps everywhere, that a refusal is said rather
                than left as a blank panel to work out. */}
            <Typography sx={{fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em",
                             color: Blue3, mb: 1}}>CHART</Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5}}>
                {offered.map(one => {
                    const chosen = one.kind === spec.kind;
                    const stopped = !!one.refusal;
                    return (
                        <Tooltip key={one.kind}
                                 title={stopped ? `This report ${one.refusal!.why}.` : one.about}>
                            {/* A SPAN, because MUI will not show a tooltip on a disabled control
                                and the reason is the entire point of disabling it. */}
                            <span>
                                <Box role="button"
                                     aria-label={one.label}
                                     aria-disabled={stopped}
                                     onClick={() => { if (!stopped) chooseKind(one.kind); }}
                                     sx={{px: 1.5, py: 0.5, borderRadius: "4px", fontSize: 13,
                                          border: "1px solid",
                                          borderColor: chosen ? Blue3 : "#E4E7EC",
                                          backgroundColor: chosen ? "#EAF6FC" : "transparent",
                                          color: stopped ? Grey5 : chosen ? Blue3 : Grey17,
                                          cursor: stopped ? "not-allowed" : "pointer",
                                          "&:hover": {backgroundColor: stopped ? "transparent"
                                                                               : "#FAFBFC"}}}>
                                    {one.label}
                                </Box>
                            </span>
                        </Tooltip>
                    );
                })}
            </Box>

            <Typography sx={{fontSize: 12.5, color: Grey6, mb: 2, maxWidth: "76ch"}}>
                {capability.about}
            </Typography>

            {/* THE SHELVES, one per slot the chosen chart declares. */}
            <Box sx={{display: "grid", gap: 1.5,
                      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))"}}>
                {capability.slots.map(slot => {
                    const held = spec.encodings[slot.id] ?? [];
                    const available = availableFor(slot);
                    return (
                        <Box key={slot.id}>
                            <Typography sx={{fontSize: 11.5, fontWeight: 600,
                                             letterSpacing: "0.06em", color: Blue3, mb: 0.5}}>
                                {slot.label.toUpperCase()}
                                {slot.required ? "" : " (optional)"}
                            </Typography>

                            <Box sx={SHELF}>
                                {held.length === 0 ? (
                                    <Typography sx={{fontSize: 12, color: Grey5, px: 0.5}}>
                                        {slot.hint ?? "Nothing yet"}
                                    </Typography>
                                ) : held.map(key => {
                                    const column = columns.find(one => one.Key === key);
                                    return (
                                        <Box key={key}
                                             sx={{display: "flex", alignItems: "center", gap: 0.25,
                                                  backgroundColor: "#EAF6FC", borderRadius: "3px",
                                                  pl: 1, pr: 0.25, py: 0.25, fontSize: 12.5,
                                                  color: Grey17}}>
                                            {column?.Label ?? key}
                                            <CloseOutlined
                                                role="button"
                                                aria-label={`Remove ${column?.Label ?? key} from ${slot.label}`}
                                                onClick={() => take(slot, key)}
                                                sx={{fontSize: 14, cursor: "pointer", color: Grey6,
                                                     "&:hover": {color: "#B42318"}}}/>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* A PICKER, NOT A DRAG. The report shelves drag because the fields
                                list is long and beside them; here the candidates are the handful of
                                columns this result has, and a select is fewer gestures and
                                reachable from a keyboard without a second implementation. */}
                            {available.length > 0 ? (
                                <TextField select size="small" fullWidth value=""
                                           sx={{mt: 0.5}}
                                           slotProps={{htmlInput: {"aria-label": `Add to ${slot.label}`}}}
                                           onChange={event => put(slot, event.target.value)}>
                                    {available.map(column => (
                                        <MenuItem key={column.Key} value={column.Key}>
                                            {column.Label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <Typography sx={{fontSize: 11.5, color: Grey5, mt: 0.5}}>
                                    {held.length ? "Every column that fits is on this shelf."
                                                 : `This report has nothing ${slot.accepts === "measure"
                                                        ? "summarized" : "to group by"}.`}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {capability.options.length > 0 ? (
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mt: 2,
                          pt: 2, borderTop: "1px solid #F1F4F7"}}>
                    {capability.options.map(option => {
                        // GREYED, NOT GONE -- see the note on DependsOn in chartSpec.
                        const off = option.kind !== "switch" && !!option.enabledWhen
                                    && !spec.options[option.enabledWhen];
                        if (option.kind === "switch") {
                            return (
                                <Box key={option.id}
                                     sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <Switch size="small" checked={!!spec.options[option.id]}
                                            slotProps={{input: {"aria-label": option.label}}}
                                            onChange={event =>
                                                setOption(option.id, event.target.checked)}/>
                                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                                        {option.label}
                                    </Typography>
                                </Box>
                            );
                        }
                        if (option.kind === "number") {
                            // A NUMBER THE READER NUDGES, which is why it has a step and bounds
                            // rather than being free text: these are ratios of a band, and 12
                            // means "twelve times the bar" rather than anything anybody wanted.
                            return (
                                <TextField key={option.id} size="small" type="number"
                                           label={option.label} sx={{width: 150}} disabled={off}
                                           value={spec.options[option.id] ?? option.default}
                                           slotProps={{htmlInput: {"aria-label": option.label,
                                                                   min: option.min,
                                                                   max: option.max,
                                                                   step: option.step}}}
                                           onChange={event => {
                                               const value = Number(event.target.value);
                                               if (Number.isNaN(value)) return;
                                               setOption(option.id,
                                                   Math.min(option.max, Math.max(option.min, value)));
                                           }}/>
                            );
                        }
                        return (
                            <TextField key={option.id} select size="small" label={option.label}
                                       sx={{width: 150}} disabled={off}
                                       value={(spec.options[option.id] as string) ?? option.default}
                                       onChange={event => setOption(option.id, event.target.value)}>
                                {option.choices.map(choice => (
                                    <MenuItem key={choice.id} value={choice.id}>
                                        {choice.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        );
                    })}
                </Box>
            ) : null}

            {/* AXES AND SERIES, for the charts that have them. Below the options rather than
                beside them: the options are one-press choices about the whole chart, and this is
                a row per measure. */}
            {capability.tunable ? (
                <ChartTuning spec={spec} columns={columns} onChange={onChange}/>
            ) : null}
        </Box>
    );
}

/** Every chart kind, for anything that needs the list without a result to check it against. */
export const CHART_KINDS = CAPABILITIES.map(one => one.kind);
